import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ref, onValue, remove, get, push, set } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, database, firestore, storage } from "../../../firebaseConfig";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { X, User, Phone, MapPin, CreditCard, DollarSign, FileText } from "lucide-react";
import jsPDF from "jspdf";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import { maskPhoneNumber } from "../../../utils/dataMasking";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const CURRENCY_CODE = "KES";
const PDF_PAGE_BREAK_Y = 275;
const PDF_TOP_MARGIN_Y = 20;
const PDF_LEFT_MARGIN_X = 14;
const PDF_ROW_HEIGHT = 8;
const RECORDS_PER_PAGE_OPTIONS = [10, 20];
const MAX_INVOICE_ITEMS = 8;
const INVOICE_TRACKING_CODE_LENGTH = 16;
const INVOICE_TRACKING_CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const WATERMARK_COLOR = [229, 231, 235];
const WATERMARK_FONT_SIZE = 40;
const WATERMARK_CENTER_X = 105;
const WATERMARK_CENTER_Y = 170;
const WATERMARK_ANGLE = 45;
const INVOICE_STATUS_UNPAID = "unpaid";
const GENERATED_AT_LABEL = "Generated:";

export const generateInvoiceTrackingCode = (length = INVOICE_TRACKING_CODE_LENGTH) => {
  const normalizedLength = Number.isFinite(Number(length))
    ? Math.max(1, Math.floor(Number(length)))
    : INVOICE_TRACKING_CODE_LENGTH;

  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const randomBytes = new Uint8Array(normalizedLength);
    window.crypto.getRandomValues(randomBytes);
    return Array.from(randomBytes)
      .map((byte) => INVOICE_TRACKING_CODE_ALPHABET[byte % INVOICE_TRACKING_CODE_ALPHABET.length])
      .join("");
  }

  return Array.from({ length: normalizedLength }, () => {
    const randomIndex = Math.floor(Math.random() * INVOICE_TRACKING_CODE_ALPHABET.length);
    return INVOICE_TRACKING_CODE_ALPHABET[randomIndex];
  }).join("");
};

export const fitImageWithinBox = ({
  boxX,
  boxY,
  boxWidth,
  boxHeight,
  imageWidth,
  imageHeight,
  padding = 0,
}) => {
  const safePadding = Math.max(0, Number(padding) || 0);
  const safeBoxWidth = Math.max(0, Number(boxWidth) || 0);
  const safeBoxHeight = Math.max(0, Number(boxHeight) || 0);
  const usableWidth = Math.max(0, safeBoxWidth - safePadding * 2);
  const usableHeight = Math.max(0, safeBoxHeight - safePadding * 2);
  const safeImageWidth = Math.max(0, Number(imageWidth) || 0);
  const safeImageHeight = Math.max(0, Number(imageHeight) || 0);

  let width = 0;
  let height = 0;
  if (safeImageWidth > 0 && safeImageHeight > 0 && usableWidth > 0 && usableHeight > 0) {
    const boxRatio = usableWidth / usableHeight;
    const imageRatio = safeImageWidth / safeImageHeight;
    if (imageRatio > boxRatio) {
      width = usableWidth;
      height = usableWidth / imageRatio;
    } else {
      height = usableHeight;
      width = usableHeight * imageRatio;
    }
  }

  const safeBoxX = Number(boxX) || 0;
  const safeBoxY = Number(boxY) || 0;
  return {
    x: safeBoxX + safePadding + Math.max(0, (usableWidth - width) / 2),
    y: safeBoxY + safePadding + Math.max(0, (usableHeight - height) / 2),
    width: Math.max(0, width),
    height: Math.max(0, height),
  };
};

const ViewClientModal = ({ client, companyName, onClose }) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
  const yearOptions = Array.from({ length: 5 }, (_, index) =>
    String(currentYear - index)
  );

  const [recentPurchases, setRecentPurchases] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appPayments, setAppPayments] = useState([]);
  const [mpesaPayments, setMpesaPayments] = useState([]);
  const [appPaymentsLoaded, setAppPaymentsLoaded] = useState(false);
  const [mpesaPaymentsLoaded, setMpesaPaymentsLoaded] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [purchaseYear, setPurchaseYear] = useState(String(currentYear));
  const [purchaseMonth, setPurchaseMonth] = useState(currentMonth);
  const [purchaseDateFrom, setPurchaseDateFrom] = useState("");
  const [purchaseDateTo, setPurchaseDateTo] = useState("");
  const [paymentYear, setPaymentYear] = useState(String(currentYear));
  const [paymentMonth, setPaymentMonth] = useState(currentMonth);
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  const [purchasesPageSize, setPurchasesPageSize] = useState(10);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [paymentsPageSize, setPaymentsPageSize] = useState(10);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceSlogan, setInvoiceSlogan] = useState("");
  const [companyProfile, setCompanyProfile] = useState(null);
  const clientDisplayName = client?.name || client?.otherName || "Client";

  const safeFilenamePart = (value) =>
    String(value || "client").replace(/[^a-z0-9_-]/gi, "_");
  const clientFilenamePart = safeFilenamePart(clientDisplayName);
  const formatCurrency = (amount) => `${CURRENCY_CODE} ${Number(amount || 0).toLocaleString()}`;

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCsv = (filename, headers, rows) => {
    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getUserRoleAndPermissions = useCallback(async (uid, company) => {
    try {
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.companyName) {
          const employeeDoc = await getDoc(
            doc(firestore, "companies", data.companyName, "employees", uid)
          );
          if (employeeDoc.exists()) {
            return employeeDoc.data();
          }
        }
        return data;
      }
      const realtimeSnap = await get(ref(database, `Users/${uid}`));
      if (realtimeSnap.exists()) {
        return realtimeSnap.val();
      }
    } catch (error) {
      console.error("Error fetching user role for client delete:", error);
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setUserRole(null);
        return;
      }
      const roleData = await getUserRoleAndPermissions(user.uid, companyName);
      setUserRole(roleData?.role || roleData?.accountType || null);
    });
    return () => unsubscribe();
  }, [companyName, getUserRoleAndPermissions]);

  useEffect(() => {
    let mounted = true;
    const loadCompanyProfile = async () => {
      if (!companyName) {
        setCompanyProfile(null);
        return;
      }
      try {
        const companyDoc = await getDoc(doc(firestore, "companies", companyName));
        if (!mounted) return;
        setCompanyProfile(companyDoc.exists() ? companyDoc.data() : null);
      } catch (error) {
        console.error("Error loading company profile for invoice:", error);
        if (mounted) {
          setCompanyProfile(null);
        }
      }
    };
    loadCompanyProfile();
    return () => {
      mounted = false;
    };
  }, [companyName]);

  useEffect(() => {
    if (!client || !companyName) return;

    const purchasesRef = ref(database, `Companies/${companyName}/ClientPurchases/${client.id}/purchases`);
    const paymentsRef = ref(database, `Companies/${companyName}/ClientPayments/${client.id}/payments`);
    const customerRecordsRef = ref(database, `Companies/${companyName}/CustomerRecords/${client.id}/Payments`);

    const unsubPurchases = onValue(purchasesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const purchases = [];
        Object.entries(data).forEach(([year, months]) => {
          Object.entries(months || {}).forEach(([month, items]) => {
            Object.entries(items || {}).forEach(([purchaseId, purchase]) => {
              purchases.push({
                ...purchase,
                _recordId: `purchase-${year}-${month}-${purchaseId}`,
              });
            });
          });
        });
        purchases.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
        setRecentPurchases(purchases);
      } else {
        setRecentPurchases([]);
      }
    });

    const unsubAppPayments = onValue(paymentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const payments = [];
        Object.entries(data).forEach(([year, months]) => {
          Object.entries(months || {}).forEach(([month, items]) => {
            Object.entries(items || {}).forEach(([paymentId, payment]) => {
              payments.push({
                ...payment,
                date: payment.paymentDate,
                _recordId: `app-${year}-${month}-${paymentId}`,
              });
            });
          });
        });
        setAppPayments(payments);
      } else {
        setAppPayments([]);
      }
      setAppPaymentsLoaded(true);
    });

    const unsubMpesaPayments = onValue(customerRecordsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const payments = [];
        Object.entries(data).forEach(([year, months]) => {
          Object.entries(months || {}).forEach(([month, items]) => {
            Object.entries(items || {}).forEach(([paymentId, payment]) => {
              payments.push({
                ...payment,
                date: payment.date || payment.TransactionDate,
                amount: parseFloat(payment.amount || payment.Amount || 0),
                paymentMethod: "M-Pesa",
                transactionRef: payment.transactionId || payment.TransactionID,
                _recordId: `mpesa-${year}-${month}-${paymentId}`,
              });
            });
          });
        });
        setMpesaPayments(payments);
      } else {
        setMpesaPayments([]);
      }
      setMpesaPaymentsLoaded(true);
    });

    return () => {
      unsubPurchases();
      unsubAppPayments();
      unsubMpesaPayments();
    };
  }, [client, companyName]);

  // Merge and sort payments whenever appPayments or mpesaPayments changes
  useEffect(() => {
    const allPayments = [...appPayments, ...mpesaPayments];
    allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
    setRecentPayments(allPayments);

    // Only set loading to false when both payment sources have loaded
    if (appPaymentsLoaded && mpesaPaymentsLoaded) {
      setLoading(false);
    }
  }, [appPayments, mpesaPayments, appPaymentsLoaded, mpesaPaymentsLoaded]);

  const filterByMonthYear = (items, dateField, selectedYear, selectedMonth) => {
    return items.filter((item) => {
      const rawDate = item?.[dateField];
      const itemDate = rawDate ? new Date(rawDate) : null;
      if (!itemDate || Number.isNaN(itemDate.getTime())) {
        return false;
      }
      const itemYear = String(itemDate.getFullYear());
      const itemMonth = String(itemDate.getMonth() + 1).padStart(2, "0");
      return itemYear === selectedYear && itemMonth === selectedMonth;
    });
  };

  const filterByDateRange = (items, dateField, fromDate, toDate) => {
    return items.filter((item) => {
      const rawDate = item?.[dateField];
      const itemDate = rawDate ? new Date(rawDate) : null;
      if (!itemDate || Number.isNaN(itemDate.getTime())) return false;
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(`${toDate}T23:59:59`) : null;
      if (from && itemDate < from) return false;
      if (to && itemDate > to) return false;
      return true;
    });
  };

  const filteredPurchases = useMemo(() => {
    if (purchaseDateFrom || purchaseDateTo) {
      return filterByDateRange(recentPurchases, "purchaseDate", purchaseDateFrom, purchaseDateTo);
    }
    return filterByMonthYear(recentPurchases, "purchaseDate", purchaseYear, purchaseMonth);
  }, [recentPurchases, purchaseYear, purchaseMonth, purchaseDateFrom, purchaseDateTo]);

  const filteredPayments = useMemo(() => {
    if (paymentDateFrom || paymentDateTo) {
      return filterByDateRange(recentPayments, "date", paymentDateFrom, paymentDateTo);
    }
    return filterByMonthYear(recentPayments, "date", paymentYear, paymentMonth);
  }, [recentPayments, paymentYear, paymentMonth, paymentDateFrom, paymentDateTo]);

  const filteredPaymentsForPurchasePeriod = useMemo(() => {
    if (purchaseDateFrom || purchaseDateTo) {
      return filterByDateRange(recentPayments, "date", purchaseDateFrom, purchaseDateTo);
    }
    return filterByMonthYear(recentPayments, "date", purchaseYear, purchaseMonth);
  }, [recentPayments, purchaseYear, purchaseMonth, purchaseDateFrom, purchaseDateTo]);

  const totalPurchasesPages = Math.max(1, Math.ceil(filteredPurchases.length / purchasesPageSize));
  const totalPaymentsPages = Math.max(1, Math.ceil(filteredPayments.length / paymentsPageSize));

  useEffect(() => {
    setPurchasesPage(1);
  }, [purchaseYear, purchaseMonth, purchasesPageSize, purchaseDateFrom, purchaseDateTo]);

  useEffect(() => {
    setPaymentsPage(1);
  }, [paymentYear, paymentMonth, paymentsPageSize, paymentDateFrom, paymentDateTo]);

  useEffect(() => {
    if (purchasesPage > totalPurchasesPages) {
      setPurchasesPage(totalPurchasesPages);
    }
  }, [purchasesPage, totalPurchasesPages]);

  useEffect(() => {
    if (paymentsPage > totalPaymentsPages) {
      setPaymentsPage(totalPaymentsPages);
    }
  }, [paymentsPage, totalPaymentsPages]);

  const paginatedPurchases = useMemo(() => {
    const startIndex = (purchasesPage - 1) * purchasesPageSize;
    return filteredPurchases.slice(startIndex, startIndex + purchasesPageSize);
  }, [filteredPurchases, purchasesPage, purchasesPageSize]);

  const paginatedPayments = useMemo(() => {
    const startIndex = (paymentsPage - 1) * paymentsPageSize;
    return filteredPayments.slice(startIndex, startIndex + paymentsPageSize);
  }, [filteredPayments, paymentsPage, paymentsPageSize]);

  const purchaseTrendData = useMemo(() => {
    const trendByDay = new Map();

    filteredPurchases.forEach((purchase) => {
      const date = new Date(purchase?.purchaseDate);
      if (Number.isNaN(date.getTime())) return;
      const dayKey = date.toISOString().split("T")[0];
      const existing = trendByDay.get(dayKey) || {
        dayKey,
        dateLabel: date.toLocaleDateString(),
        purchaseAmount: 0,
        purchaseQuantity: 0,
        payments: [],
      };
      existing.purchaseAmount += Number(purchase?.totalAmount || 0);
      existing.purchaseQuantity += Number(purchase?.quantity || 0);
      trendByDay.set(dayKey, existing);
    });

    filteredPaymentsForPurchasePeriod.forEach((payment) => {
      const date = new Date(payment?.date);
      if (Number.isNaN(date.getTime())) return;
      const dayKey = date.toISOString().split("T")[0];
      const existing = trendByDay.get(dayKey) || {
        dayKey,
        dateLabel: date.toLocaleDateString(),
        purchaseAmount: 0,
        purchaseQuantity: 0,
        payments: [],
      };
      existing.payments.push({
        amount: Number(payment?.amount || 0),
        method: payment?.paymentMethod || "N/A",
        reference: payment?.transactionRef || "—",
      });
      trendByDay.set(dayKey, existing);
    });

    return Array.from(trendByDay.values()).sort(
      (a, b) => new Date(a.dayKey) - new Date(b.dayKey)
    );
  }, [filteredPurchases, filteredPaymentsForPurchasePeriod]);

  const handleExportPurchasesCsv = () => {
    try {
      const filename = `${clientFilenamePart}_purchases_${purchasePeriodLabel.replace(/ /g, "_")}.csv`;
      const rows = filteredPurchases.map((purchase) => [
        purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString() : "",
        purchase.quantity || 0,
        purchase.totalAmount || 0,
        purchase.paymentMode || "",
      ]);
      downloadCsv(filename, ["Date", "Quantity (L)", "Amount", "Mode"], rows);
      toast.success("Purchases CSV exported");
    } catch (error) {
      console.error("Error exporting purchases CSV:", error);
      toast.error("Failed to export purchases CSV");
    }
  };

  const handleExportPaymentsCsv = () => {
    try {
      const filename = `${clientFilenamePart}_payments_${paymentPeriodLabel.replace(/ /g, "_")}.csv`;
      const rows = filteredPayments.map((payment) => [
        payment.date ? new Date(payment.date).toLocaleDateString() : "",
        payment.amount || 0,
        payment.paymentMethod || "",
        payment.transactionRef || "",
      ]);
      downloadCsv(filename, ["Date", "Amount", "Method", "Reference"], rows);
      toast.success("Payments CSV exported");
    } catch (error) {
      console.error("Error exporting payments CSV:", error);
      toast.error("Failed to export payments CSV");
    }
  };

  const addPdfTableRows = (doc, rows, startY) => {
    let y = startY;
    rows.forEach((row) => {
      if (y > PDF_PAGE_BREAK_Y) {
        doc.addPage();
        y = PDF_TOP_MARGIN_Y;
      }
      doc.text(row.join("  |  "), PDF_LEFT_MARGIN_X, y);
      y += PDF_ROW_HEIGHT;
    });
    return y;
  };

  const handleExportPurchasesPdf = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Client Purchases - ${clientDisplayName}`, PDF_LEFT_MARGIN_X, 18);
      doc.setFontSize(11);
      doc.text(`Period: ${purchasePeriodLabel}`, PDF_LEFT_MARGIN_X, 26);
      doc.text("Date | Quantity (L) | Amount | Mode", PDF_LEFT_MARGIN_X, 36);
      const rows = filteredPurchases.map((purchase) => [
        purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString() : "",
        String(purchase.quantity || 0),
        `${CURRENCY_CODE} ${Number(purchase.totalAmount || 0).toLocaleString()}`,
        purchase.paymentMode || "",
      ]);
      addPdfTableRows(doc, rows, 46);
      doc.save(`${clientFilenamePart}_purchases_${purchasePeriodLabel.replace(/ /g, "_")}.pdf`);
      toast.success("Purchases PDF exported");
    } catch (error) {
      console.error("Error exporting purchases PDF:", error);
      toast.error("Failed to export purchases PDF");
    }
  };

  const handleExportPaymentsPdf = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Client Payments - ${clientDisplayName}`, PDF_LEFT_MARGIN_X, 18);
      doc.setFontSize(11);
      doc.text(`Period: ${paymentPeriodLabel}`, PDF_LEFT_MARGIN_X, 26);
      doc.text("Date | Amount | Method | Reference", PDF_LEFT_MARGIN_X, 36);
      const rows = filteredPayments.map((payment) => [
        payment.date ? new Date(payment.date).toLocaleDateString() : "",
        `${CURRENCY_CODE} ${Number(payment.amount || 0).toLocaleString()}`,
        payment.paymentMethod || "",
        payment.transactionRef || "",
      ]);
      addPdfTableRows(doc, rows, 46);
      doc.save(`${clientFilenamePart}_payments_${paymentPeriodLabel.replace(/ /g, "_")}.pdf`);
      toast.success("Payments PDF exported");
    } catch (error) {
      console.error("Error exporting payments PDF:", error);
      toast.error("Failed to export payments PDF");
    }
  };

  const handleClearPurchaseDates = () => {
    setPurchaseDateFrom("");
    setPurchaseDateTo("");
  };

  const handleClearPaymentDates = () => {
    setPaymentDateFrom("");
    setPaymentDateTo("");
  };

  const handleDeleteClient = async () => {
    const confirmationTargets = [
      companyName?.toLowerCase(),
      client?.otherName?.toLowerCase(),
    ].filter(Boolean);

    if (!confirmationTargets.includes(deleteInput.trim().toLowerCase())) {
      toast.error(
        `Type "${client?.otherName}" or "${companyName}" to confirm deletion`
      );
      return;
    }

    if (userRole !== "CompanyAdmin") {
      toast.error("Only CompanyAdmin can delete clients");
      return;
    }
    if (!companyName) {
      toast.error("Company name not available for deletion");
      return;
    }

    setDeleting(true);
    const clientKey = client?.otherName;
    const clientIdKey = client?.id;
    if (!clientKey || !clientIdKey) {
      toast.error("Missing client identifier");
      setDeleting(false);
      return;
    }
    try {
      const removalPaths = [
        `Companies/${companyName}/ValidatedClients/${clientKey}`,
        `Companies/${companyName}/AIValidatedClients/${clientKey}`,
        `Companies/${companyName}/NewValidatedClients/${clientKey}`,
        `Companies/${companyName}/ClientPurchases/${clientIdKey}`,
        `Companies/${companyName}/ClientPayments/${clientIdKey}`,
        `Companies/${companyName}/CustomerRecords/${clientIdKey}`,
      ];

      await Promise.all(removalPaths.map((path) => remove(ref(database, path))));
      await logRecordAuditEvent({
        action: "delete",
        entityType: "client",
        companyName,
        recordId: clientIdKey || clientKey,
        sourceComponent: "ViewClientModal",
        oldData: client,
      });
      toast.success("Client deleted successfully");
      setShowDeleteConfirm(false);
      setDeleteInput("");
      onClose();
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  if (!client) return null;

  const totalPurchases = recentPurchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
  const totalPayments = recentPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const canDeleteClient = userRole === "CompanyAdmin";
  const paymentTrendDays = purchaseTrendData.filter((item) => item.payments.length > 0);
  const invoiceMonthLabel = MONTH_OPTIONS.find((option) => option.value === purchaseMonth)?.label || purchaseMonth;
  const purchaseDateRangeActive = !!(purchaseDateFrom || purchaseDateTo);
  const paymentDateRangeActive = !!(paymentDateFrom || paymentDateTo);

  const formatDateRangeLabel = (from, to) => {
    if (from && to) return `${from} to ${to}`;
    if (from) return `From ${from}`;
    return `Until ${to}`;
  };

  const purchasePeriodLabel = purchaseDateRangeActive
    ? formatDateRangeLabel(purchaseDateFrom, purchaseDateTo)
    : `${purchaseYear}-${purchaseMonth}`;
  const paymentPeriodLabel = paymentDateRangeActive
    ? formatDateRangeLabel(paymentDateFrom, paymentDateTo)
    : `${paymentYear}-${paymentMonth}`;
  const invoicePeriodLabel = purchaseDateRangeActive
    ? formatDateRangeLabel(purchaseDateFrom, purchaseDateTo)
    : `${invoiceMonthLabel} ${purchaseYear}`;
  const invoicePurchases = filteredPurchases;
  const invoicePayments = filteredPaymentsForPurchasePeriod;
  const invoicePurchasesTotal = invoicePurchases.reduce((sum, item) => sum + Number(item?.totalAmount || 0), 0);
  const invoicePaymentsTotal = invoicePayments.reduce((sum, item) => sum + Number(item?.amount || 0), 0);

  const purchaseTrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0]?.payload;
    if (!entry) return null;

    return (
      <div className="bg-white border rounded shadow p-2 text-xs max-w-xs">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        <p className="text-gray-600">
          Purchases: {formatCurrency(entry.purchaseAmount)}
        </p>
        <p className="text-gray-600 mb-1">
          Quantity: {entry.purchaseQuantity.toLocaleString()} L
        </p>
        {entry.payments.length > 0 ? (
          <div className="space-y-1">
            <p className="font-semibold text-green-700">Payments</p>
            {entry.payments.map((payment, index) => (
              <p
                key={`${entry.dayKey}-${payment.reference}-${payment.amount}-${index}`}
                className="text-gray-600"
              >
                {payment.method}: {formatCurrency(payment.amount)} ({payment.reference})
              </p>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No payments on this day</p>
        )}
      </div>
    );
  };

  const drawInvoiceGridTable = (doc, title, headers, rows, startY) => {
    const tableX = 14;
    const tableWidth = 182;
    const rowHeight = 8;
    const headerHeight = 8;
    const titleHeight = 8;
    const columnWidth = tableWidth / headers.length;
    const messageColumnIndex = Math.floor(headers.length / 2);
    const fallbackRow = Array.from({ length: headers.length }, (_, index) =>
      index === messageColumnIndex ? "No records" : "—"
    );
    const safeRows = rows.length ? rows : [fallbackRow];
    const tableHeight = titleHeight + headerHeight + rowHeight * safeRows.length;

    // Title bar — white bg with orange left accent
    doc.setFillColor(255, 255, 255);
    doc.rect(tableX, startY, tableWidth, titleHeight, "F");
    doc.setFillColor(249, 115, 22); // orange-500
    doc.rect(tableX, startY, 4, titleHeight, "F");
    doc.setTextColor(17, 24, 39);
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(title, tableX + 7, startY + 5.5);
    doc.setFont(undefined, "normal");

    // Header row — light orange bg
    const headerY = startY + titleHeight;
    doc.setFillColor(255, 237, 213); // orange-100
    doc.rect(tableX, headerY, tableWidth, headerHeight, "F");
    doc.setTextColor(8, 145, 178); // cyan-600 for labels
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    headers.forEach((header, index) => {
      doc.text(header.toUpperCase(), tableX + index * columnWidth + 3, headerY + 5.5);
    });
    doc.setFont(undefined, "normal");

    // Data rows
    safeRows.forEach((row, rowIndex) => {
      const rowY = headerY + headerHeight + rowIndex * rowHeight;
      if (rowIndex % 2 === 0) {
        doc.setFillColor(255, 255, 255);
      } else {
        doc.setFillColor(255, 251, 235); // amber-50
      }
      doc.rect(tableX, rowY, tableWidth, rowHeight, "F");
      doc.setTextColor(17, 24, 39); // black data
      doc.setFontSize(9);
      row.forEach((value, columnIndex) => {
        doc.text(String(value ?? "—"), tableX + columnIndex * columnWidth + 3, rowY + 5.5);
      });
    });

    // Orange outer border and lines
    doc.setDrawColor(249, 115, 22); // orange-500
    doc.setLineWidth(0.5);
    doc.rect(tableX, startY, tableWidth, tableHeight);
    // Header bottom separator
    doc.setDrawColor(249, 115, 22);
    doc.line(tableX, headerY + headerHeight, tableX + tableWidth, headerY + headerHeight);
    // Title bottom separator
    doc.setDrawColor(249, 115, 22);
    doc.line(tableX, headerY, tableX + tableWidth, headerY);
    // Column dividers (light orange)
    doc.setDrawColor(253, 186, 116); // orange-300
    for (let columnIndex = 1; columnIndex < headers.length; columnIndex += 1) {
      const lineX = tableX + columnIndex * columnWidth;
      doc.line(lineX, headerY, lineX, startY + tableHeight);
    }
    // Row dividers
    safeRows.forEach((_, rowIndex) => {
      if (rowIndex > 0) {
        const rowY = headerY + headerHeight + rowIndex * rowHeight;
        doc.line(tableX, rowY, tableX + tableWidth, rowY);
      }
    });

    return startY + tableHeight + 8;
  };

  const handleGenerateInvoicePdf = async () => {
    const parsedInvoiceAmount = Number(invoiceAmount);
    if (!invoiceAmount || Number.isNaN(parsedInvoiceAmount) || parsedInvoiceAmount <= 0) {
      toast.error("Enter a valid invoice amount");
      return;
    }

    try {
      const invoiceDoc = new jsPDF();
      const trackingCode = generateInvoiceTrackingCode();
      const companyHeaderName = companyProfile?.companyName || companyName || "Company";
      const companyEmail = companyProfile?.email || "—";
      const companyPhone = companyProfile?.phone || "—";
      const companyAddress = companyProfile?.address || "";
      const companyIndustry = companyProfile?.industry || "";
      const invoiceClientIdentifier = client?.otherName || client?.id || clientFilenamePart;
      const generatedAt = new Date();
      const invoiceDateIso = generatedAt.toISOString();
      const invoiceDateOnly = invoiceDateIso.split("T")[0];
      const generatedAtLabel = generatedAt.toLocaleString();
      const pdfFilename = `${clientFilenamePart}_invoice_${purchaseYear}_${purchaseMonth}_${trackingCode}.pdf`;
      const companyStoragePart = safeFilenamePart(companyHeaderName);
      const invoiceStoragePath = `companies/${companyStoragePart}/client-invoices/${safeFilenamePart(
        invoiceClientIdentifier
      )}/${purchaseYear}/${purchaseMonth}/${trackingCode}.pdf`;

      // Watermark to identify Neuracraft-generated invoices
      invoiceDoc.setTextColor(...WATERMARK_COLOR);
      invoiceDoc.setFontSize(WATERMARK_FONT_SIZE);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text("NEURACRAFT VERIFIED", WATERMARK_CENTER_X, WATERMARK_CENTER_Y, {
        align: "center",
        angle: WATERMARK_ANGLE,
      });
      invoiceDoc.setFont(undefined, "normal");

      // --- HEADER: white background, logo zone on the right ---
      const headerHeight = 50;
      invoiceDoc.setFillColor(255, 255, 255);
      invoiceDoc.rect(0, 0, 210, headerHeight, "F");

      // Logo zone — soft orange-tinted box on the far right
      const logoBoxX = 155;
      const logoBoxY = 4;
      const logoBoxW = 42;
      const logoBoxH = 38;
      invoiceDoc.setFillColor(255, 247, 237); // orange-50 background
      invoiceDoc.setDrawColor(253, 186, 116); // orange-300 border
      invoiceDoc.setLineWidth(0.4);
      invoiceDoc.roundedRect(logoBoxX, logoBoxY, logoBoxW, logoBoxH, 2, 2, "FD");

      // Always use fallback company icon in the logo zone.
      const iconCenterX = logoBoxX + logoBoxW / 2;
      const iconCenterY = logoBoxY + logoBoxH / 2;
      invoiceDoc.setFillColor(255, 237, 213);
      invoiceDoc.circle(iconCenterX, iconCenterY, 10, "F");
      invoiceDoc.setFontSize(16);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.setTextColor(249, 115, 22);
      const initial = (companyHeaderName || "C").charAt(0).toUpperCase();
      invoiceDoc.text(initial, iconCenterX, iconCenterY + 2, { align: "center" });
      invoiceDoc.setFont(undefined, "normal");

      // Company name — left side
      invoiceDoc.setTextColor(17, 24, 39);
      invoiceDoc.setFontSize(18);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text(companyHeaderName, 14, 16);
      invoiceDoc.setFont(undefined, "normal");

      // Industry / tagline below company name
      if (companyIndustry) {
        invoiceDoc.setFontSize(9);
        invoiceDoc.setTextColor(107, 114, 128); // gray-500
        invoiceDoc.text(companyIndustry, 14, 23);
      }

      // Contact info — cyan
      invoiceDoc.setFontSize(9);
      invoiceDoc.setTextColor(8, 145, 178); // cyan-600
      const contactParts = [
        companyEmail !== "—" ? companyEmail : null,
        companyPhone !== "—" ? companyPhone : null,
        companyAddress || null,
      ].filter(Boolean);
      if (contactParts.length > 0) {
        invoiceDoc.text(contactParts.join("   ·   "), 14, 31);
      }

      // "INVOICE" label below contacts, in orange bold
      invoiceDoc.setFontSize(9);
      invoiceDoc.setTextColor(249, 115, 22);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text("INVOICE", 14, 40);
      invoiceDoc.setFont(undefined, "normal");

      // Orange accent line under header
      invoiceDoc.setFillColor(249, 115, 22);
      invoiceDoc.rect(0, headerHeight, 210, 2, "F");

      // --- CLIENT & PERIOD INFO ---
      invoiceDoc.setTextColor(17, 24, 39);
      invoiceDoc.setFontSize(11);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text(`Bill To: ${clientDisplayName}`, 14, 64);
      invoiceDoc.setFont(undefined, "normal");
      invoiceDoc.setFontSize(10);
      invoiceDoc.setTextColor(107, 114, 128);
      invoiceDoc.text(`Period: ${invoicePeriodLabel}`, 14, 71);
      invoiceDoc.setFontSize(9);
      invoiceDoc.setTextColor(249, 115, 22);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text(`Tracking Code: ${trackingCode}`, 14, 77);
      invoiceDoc.setFont(undefined, "normal");
      invoiceDoc.setFontSize(8);
      invoiceDoc.setTextColor(107, 114, 128);
      invoiceDoc.text(`${GENERATED_AT_LABEL} ${generatedAtLabel}`, 14, 82);

      // Invoice amount — right aligned block
      invoiceDoc.setFillColor(255, 237, 213); // orange-100
      invoiceDoc.rect(130, 58, 66, 18, "F");
      invoiceDoc.setDrawColor(249, 115, 22);
      invoiceDoc.setLineWidth(0.5);
      invoiceDoc.rect(130, 58, 66, 18);
      invoiceDoc.setFontSize(8);
      invoiceDoc.setTextColor(8, 145, 178);
      invoiceDoc.text("INVOICE AMOUNT", 133, 64);
      invoiceDoc.setFontSize(12);
      invoiceDoc.setTextColor(17, 24, 39);
      invoiceDoc.setFont(undefined, "bold");
      invoiceDoc.text(formatCurrency(parsedInvoiceAmount), 133, 72);
      invoiceDoc.setFont(undefined, "normal");

      // Thin separator before tables
      invoiceDoc.setDrawColor(253, 186, 116); // orange-300
      invoiceDoc.setLineWidth(0.3);
      invoiceDoc.line(14, 82, 196, 82);

      // --- TABLES: ALL records sorted ascending by date ---
      const sortedPurchaseRows = [...invoicePurchases]
        .sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate))
        .map((purchase) => [
          purchase?.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString() : "—",
          `${Number(purchase?.quantity || 0)}L`,
          formatCurrency(purchase?.totalAmount || 0),
          purchase?.paymentMode || "—",
        ]);

      const sortedPaymentRows = [...invoicePayments]
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((payment) => [
          payment?.date ? new Date(payment.date).toLocaleDateString() : "—",
          formatCurrency(payment?.amount || 0),
          payment?.paymentMethod || "N/A",
          payment?.transactionRef || "—",
        ]);

      let y = 88;
      y = drawInvoiceGridTable(
        invoiceDoc,
        `Purchases  (${invoicePurchases.length} records)`,
        ["Date", "Quantity", "Amount", "Mode"],
        sortedPurchaseRows,
        y
      );
      y = drawInvoiceGridTable(
        invoiceDoc,
        `Payments  (${invoicePayments.length} records)`,
        ["Date", "Amount", "Method", "Reference"],
        sortedPaymentRows,
        y
      );

      // --- TOTALS SUMMARY ---
      y += 2;
      invoiceDoc.setDrawColor(249, 115, 22);
      invoiceDoc.setLineWidth(0.4);
      invoiceDoc.line(14, y, 196, y);
      y += 6;
      invoiceDoc.setFontSize(10);
      invoiceDoc.setTextColor(8, 145, 178);
      invoiceDoc.text(
        `Purchases total: ${formatCurrency(invoicePurchasesTotal)}     Payments total: ${formatCurrency(invoicePaymentsTotal)}`,
        14,
        y
      );

      // --- SLOGAN ---
      const finalSlogan = invoiceSlogan.trim();
      if (finalSlogan) {
        y += 8;
        invoiceDoc.setDrawColor(253, 186, 116);
        invoiceDoc.setLineWidth(0.3);
        invoiceDoc.line(14, y - 2, 196, y - 2);
        invoiceDoc.setTextColor(107, 114, 128);
        invoiceDoc.setFontSize(10);
        invoiceDoc.setFont(undefined, "italic");
        invoiceDoc.text(`"${finalSlogan}"`, 14, y + 4);
        invoiceDoc.setFont(undefined, "normal");
        invoiceDoc.setFontSize(8);
        invoiceDoc.setTextColor(156, 163, 175);
        invoiceDoc.text(`${GENERATED_AT_LABEL} ${generatedAtLabel}`, 196, y + 4, { align: "right" });
      }

      // --- STAMP & SIGNATURE SECTION ---
      invoiceDoc.setDrawColor(253, 186, 116);
      invoiceDoc.setLineWidth(0.4);
      invoiceDoc.roundedRect(130, 246, 66, 30, 2, 2);
      invoiceDoc.setFontSize(8);
      invoiceDoc.setTextColor(8, 145, 178);
      invoiceDoc.text("Stamp & Signature", 133, 252);
      invoiceDoc.setDrawColor(209, 213, 219);
      invoiceDoc.line(133, 262, 193, 262);
      invoiceDoc.line(133, 272, 193, 272);
      invoiceDoc.setTextColor(156, 163, 175);
      invoiceDoc.text("", 133, 260);
      invoiceDoc.text("", 133, 270);

      // --- FOOTER ---
      invoiceDoc.setFillColor(249, 115, 22);
      invoiceDoc.rect(0, 282, 210, 2, "F");
      invoiceDoc.setTextColor(8, 145, 178);
      invoiceDoc.setFontSize(9);
      invoiceDoc.text("Thank you for your business!", 14, 291);
      invoiceDoc.setTextColor(156, 163, 175);
      invoiceDoc.text(
        `This is a computer-generated invoice from Neuracraft | Tracking: ${trackingCode}`,
        14,
        296
      );
      invoiceDoc.text(`Timestamp: ${generatedAtLabel}`, 196, 296, { align: "right" });

      if (!storage) {
        throw new Error("Firebase storage is not configured");
      }

      const invoiceBlob = invoiceDoc.output("blob");
      const uploadedInvoiceRef = storageRef(storage, invoiceStoragePath);
      await uploadBytes(uploadedInvoiceRef, invoiceBlob, {
        contentType: "application/pdf",
        customMetadata: {
          trackingCode,
          generatedAt: invoiceDateIso,
          companyName: String(companyName || ""),
          clientId: String(invoiceClientIdentifier),
          period: `${purchaseYear}-${purchaseMonth}`,
        },
      });
      const invoiceDownloadUrl = await getDownloadURL(uploadedInvoiceRef);

      const invoiceVerificationPayload = {
        invoiceNumber: `INV-${trackingCode}`,
        trackingCode,
        invoiceDate: invoiceDateOnly,
        generatedAt: invoiceDateIso,
        amount: parsedInvoiceAmount,
        status: INVOICE_STATUS_UNPAID,
        description: `Generated invoice PDF for ${invoicePeriodLabel}`,
        companyName: companyHeaderName,
        clientId: invoiceClientIdentifier,
        clientName: clientDisplayName,
        pdfUrl: invoiceDownloadUrl,
        pdfStoragePath: invoiceStoragePath,
        pdfFileName: pdfFilename,
        period: purchasePeriodLabel,
        slogan: finalSlogan || "",
        purchasesTotal: invoicePurchasesTotal,
        paymentsTotal: invoicePaymentsTotal,
        purchases: invoicePurchases.map((purchase) => ({
          purchaseDate: purchase?.purchaseDate || null,
          quantity: Number(purchase?.quantity || 0),
          totalAmount: Number(purchase?.totalAmount || 0),
          paymentMode: purchase?.paymentMode || "—",
        })),
        payments: invoicePayments.map((payment) => ({
          date: payment?.date || null,
          amount: Number(payment?.amount || 0),
          paymentMethod: payment?.paymentMethod || "N/A",
          transactionRef: payment?.transactionRef || "—",
        })),
        watermarkText: `NEURACRAFT VERIFIED - ${trackingCode}`,
      };

      if (firestore) {
        await setDoc(doc(firestore, "invoice_verifications", trackingCode), {
          ...invoiceVerificationPayload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      if (database && companyName && invoiceClientIdentifier) {
        const invoiceRecordsRef = ref(
          database,
          `Companies/${companyName}/ClientInvoices/${invoiceClientIdentifier}/invoices`
        );
        const invoiceRecordRef = push(invoiceRecordsRef);
        await set(invoiceRecordRef, {
          invoiceNumber: `INV-${trackingCode}`,
          trackingCode,
          invoiceDate: invoiceDateOnly,
          amount: parsedInvoiceAmount,
          status: INVOICE_STATUS_UNPAID,
          description: `Generated invoice PDF for ${invoicePeriodLabel}`,
          companyName: companyHeaderName,
          clientId: invoiceClientIdentifier,
          clientName: clientDisplayName,
          generatedAt: invoiceDateIso,
          pdfUrl: invoiceDownloadUrl,
          pdfStoragePath: invoiceStoragePath,
          pdfFileName: pdfFilename,
          period: purchasePeriodLabel,
          generatedBy: "ViewClientModal",
        });
      } else {
        console.warn("Skipped invoice metadata save: missing database, companyName, or client id");
      }

      invoiceDoc.save(pdfFilename);
      toast.success(`Invoice PDF generated. Tracking code: ${trackingCode}`);
      setShowInvoiceModal(false);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      toast.error("Failed to generate invoice PDF");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <User size={24} />
            Client Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Client Information */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Client Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <User className="text-cyan-600" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium">{client.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="text-cyan-600" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                   <p className="font-medium">
                    {client.phone
                      ? (userRole === "CompanyAdmin" ? client.phone : maskPhoneNumber(client.phone))
                      : "N/A"}
                   </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-cyan-600" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-medium">{client.address || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="text-cyan-600" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Route</p>
                  <p className="font-medium">{client.route || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="text-cyan-600" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Paybill Number</p>
                  <p className="font-medium">{client.paybill  || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Current Balance</p>
                <DollarSign className="text-amber-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-amber-600">
                KES {parseFloat(client.balance || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Purchases</p>
                <DollarSign className="text-cyan-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-cyan-600">
                KES {totalPurchases.toLocaleString()}
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Payments</p>
                <DollarSign className="text-green-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-green-600">
                KES {totalPayments.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h4 className="font-semibold text-gray-800">Purchases Trend by Day</h4>
              <button
                type="button"
                onClick={() => setShowInvoiceModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gradient-to-r from-indigo-600 to-cyan-600 text-white text-sm hover:opacity-90 transition"
              >
                <FileText size={16} />
                Generate Invoice
              </button>
            </div>
            {loading ? (
              <p className="text-sm text-gray-500">Loading chart...</p>
            ) : purchaseTrendData.length === 0 ? (
              <p className="text-sm text-gray-500">
                No purchases trend data for selected purchases period.
              </p>
            ) : (
              <div className="h-64" aria-label="Purchases trend chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={purchaseTrendData} margin={{ top: 12, right: 16, left: 6, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={purchaseTrendTooltip} />
                    {paymentTrendDays.map((item) => (
                      <ReferenceLine
                        key={`payment-day-${item.dayKey}`}
                        x={item.dateLabel}
                        stroke="#22c55e"
                        strokeDasharray="5 5"
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="purchaseAmount"
                      stroke="#0891b2"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name="Purchases"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Dashed green lines mark days where payments were made. Hover points for payment details.
            </p>
          </div>

          {/* Purchases */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h4 className="font-semibold text-gray-800">Purchases</h4>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Purchases year"
                  value={purchaseYear}
                  onChange={(e) => setPurchaseYear(e.target.value)}
                  className={`border rounded px-2 py-1 text-sm ${purchaseDateRangeActive ? "opacity-40 cursor-not-allowed" : ""}`}
                  disabled={purchaseDateRangeActive}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Purchases month"
                  value={purchaseMonth}
                  onChange={(e) => setPurchaseMonth(e.target.value)}
                  className={`border rounded px-2 py-1 text-sm ${purchaseDateRangeActive ? "opacity-40 cursor-not-allowed" : ""}`}
                  disabled={purchaseDateRangeActive}
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-400 hidden sm:inline">|</span>
                <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                <input
                  type="date"
                  aria-label="Purchases date from"
                  value={purchaseDateFrom}
                  onChange={(e) => setPurchaseDateFrom(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                <input
                  type="date"
                  aria-label="Purchases date to"
                  value={purchaseDateTo}
                  onChange={(e) => setPurchaseDateTo(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                {(purchaseDateFrom || purchaseDateTo) && (
                  <button
                    type="button"
                    onClick={handleClearPurchaseDates}
                    className="px-2 py-1 rounded bg-gray-100 text-gray-500 text-xs hover:bg-gray-200 transition"
                    title="Clear date range"
                  >
                    ✕ Clear
                  </button>
                )}
                <select
                  aria-label="Purchases records per page"
                  value={String(purchasesPageSize)}
                  onChange={(e) => setPurchasesPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {RECORDS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={`purchase-size-${option}`} value={option}>
                      Show {option}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleExportPurchasesCsv}
                  className="px-3 py-1 rounded bg-cyan-100 text-cyan-700 text-sm hover:bg-cyan-200 transition"
                  disabled={loading}
                >
                  Export CSV
                </button>
                <button
                  onClick={handleExportPurchasesPdf}
                  className="px-3 py-1 rounded bg-indigo-100 text-indigo-700 text-sm hover:bg-indigo-200 transition"
                  disabled={loading}
                >
                  Export PDF
                </button>
              </div>
            </div>
            <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
              {loading ? (
                <p className="p-4 text-center text-gray-500">Loading...</p>
              ) : filteredPurchases.length === 0 ? (
                <p className="p-4 text-center text-gray-500">No purchases yet</p>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-orange-400 bg-orange-50">
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Date</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Quantity (L)</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Amount</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPurchases.map((purchase, idx) => (
                        <tr key={purchase._recordId} className={`border-b border-orange-100 hover:bg-orange-50/40 transition ${idx % 2 === 1 ? "bg-orange-50/20" : ""}`}>
                          <td className="p-3 text-gray-900 font-medium">
                            {new Date(purchase.purchaseDate).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-gray-900">{purchase.quantity}</td>
                          <td className="p-3 text-gray-900 font-semibold">
                            {formatCurrency(purchase.totalAmount)}
                          </td>
                          <td className="p-3 text-gray-700">{purchase.paymentMode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between px-3 py-2 border-t border-orange-200 bg-orange-50/40 text-xs text-gray-600">
                    <p className="text-cyan-600 font-medium">
                      Page {purchasesPage} of {totalPurchasesPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPurchasesPage((prev) => Math.max(1, prev - 1))}
                        disabled={purchasesPage === 1}
                        className="px-2 py-1 border border-orange-300 rounded text-orange-700 disabled:opacity-40 hover:bg-orange-100 transition"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPurchasesPage((prev) => Math.min(totalPurchasesPages, prev + 1))
                        }
                        disabled={purchasesPage === totalPurchasesPages}
                        className="px-2 py-1 border border-orange-300 rounded text-orange-700 disabled:opacity-40 hover:bg-orange-100 transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Payments */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h4 className="font-semibold text-gray-800">Payments</h4>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Payments year"
                  value={paymentYear}
                  onChange={(e) => setPaymentYear(e.target.value)}
                  className={`border rounded px-2 py-1 text-sm ${paymentDateRangeActive ? "opacity-40 cursor-not-allowed" : ""}`}
                  disabled={paymentDateRangeActive}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Payments month"
                  value={paymentMonth}
                  onChange={(e) => setPaymentMonth(e.target.value)}
                  className={`border rounded px-2 py-1 text-sm ${paymentDateRangeActive ? "opacity-40 cursor-not-allowed" : ""}`}
                  disabled={paymentDateRangeActive}
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-400 hidden sm:inline">|</span>
                <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                <input
                  type="date"
                  aria-label="Payments date from"
                  value={paymentDateFrom}
                  onChange={(e) => setPaymentDateFrom(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                <input
                  type="date"
                  aria-label="Payments date to"
                  value={paymentDateTo}
                  onChange={(e) => setPaymentDateTo(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                {(paymentDateFrom || paymentDateTo) && (
                  <button
                    type="button"
                    onClick={handleClearPaymentDates}
                    className="px-2 py-1 rounded bg-gray-100 text-gray-500 text-xs hover:bg-gray-200 transition"
                    title="Clear date range"
                  >
                    ✕ Clear
                  </button>
                )}
                <select
                  aria-label="Payments records per page"
                  value={String(paymentsPageSize)}
                  onChange={(e) => setPaymentsPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {RECORDS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={`payments-size-${option}`} value={option}>
                      Show {option}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleExportPaymentsCsv}
                  className="px-3 py-1 rounded bg-cyan-100 text-cyan-700 text-sm hover:bg-cyan-200 transition"
                  disabled={loading}
                >
                  Export CSV
                </button>
                <button
                  onClick={handleExportPaymentsPdf}
                  className="px-3 py-1 rounded bg-indigo-100 text-indigo-700 text-sm hover:bg-indigo-200 transition"
                  disabled={loading}
                >
                  Export PDF
                </button>
              </div>
            </div>
            <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
              {loading ? (
                <p className="p-4 text-center text-gray-500">Loading...</p>
              ) : filteredPayments.length === 0 ? (
                <p className="p-4 text-center text-gray-500">No payments yet</p>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-orange-400 bg-orange-50">
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Date</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Amount</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Method</th>
                        <th className="text-left p-3 text-xs font-semibold text-cyan-600 uppercase tracking-wide">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPayments.map((payment, idx) => (
                        <tr key={payment._recordId} className={`border-b border-orange-100 hover:bg-orange-50/40 transition ${idx % 2 === 1 ? "bg-orange-50/20" : ""}`}>
                          <td className="p-3 text-gray-900 font-medium">
                            {new Date(payment.date).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-gray-900 font-semibold">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="p-3 text-gray-900">{payment.paymentMethod}</td>
                          <td className="p-3 text-xs text-gray-500">
                            {payment.transactionRef || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between px-3 py-2 border-t border-orange-200 bg-orange-50/40 text-xs text-gray-600">
                    <p className="text-cyan-600 font-medium">
                      Page {paymentsPage} of {totalPaymentsPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentsPage((prev) => Math.max(1, prev - 1))}
                        disabled={paymentsPage === 1}
                        className="px-2 py-1 border border-orange-300 rounded text-orange-700 disabled:opacity-40 hover:bg-orange-100 transition"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPaymentsPage((prev) => Math.min(totalPaymentsPages, prev + 1))
                        }
                        disabled={paymentsPage === totalPaymentsPages}
                        className="px-2 py-1 border border-orange-300 rounded text-orange-700 disabled:opacity-40 hover:bg-orange-100 transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Client Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className={`font-semibold ${client.status === "Active" ? "text-green-600" : "text-yellow-600"}`}>
                {client.status}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Mode</p>
              <p className="font-semibold text-gray-800">{client.mode}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Created</p>
              <p className="text-sm text-gray-800">
                {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </div>
          {canDeleteClient ? (
            <div className="mt-6 bg-red-50 border border-red-200 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Danger zone</h4>
              <p className="text-sm text-red-700 mb-4">
                Deleting this client will remove their validated record and purchase/payment history for{" "}
                <span className="font-semibold">{companyName}</span>. This cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete Client
              </button>
            </div>
          ) : userRole ? (
            <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                Only a CompanyAdmin can delete client records.
              </p>
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
          >
            Close
          </button>
        </div>
      </div>
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl border border-slate-200 overflow-hidden">
            {/* Invoice modal header — company branding strip */}
            <div className="px-6 pt-6 pb-4 border-b border-orange-100">
              <div className="flex items-center gap-4">
                {/* Company logo */}
                <div className="w-16 h-16 rounded-xl border-2 border-orange-200 overflow-hidden flex-shrink-0 flex items-center justify-center bg-orange-50">
                  {companyProfile?.logoUrl ? (
                    <img src={companyProfile.logoUrl} alt="Company logo" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-orange-400 font-bold text-2xl">
                      {(companyProfile?.companyName || companyName || "C").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {/* Company info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-bold text-gray-900 truncate">
                    {companyProfile?.companyName || companyName || "Company"}
                  </h4>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                    {companyProfile?.email && (
                      <span className="text-xs text-cyan-600">{companyProfile.email}</span>
                    )}
                    {companyProfile?.phone && (
                      <span className="text-xs text-cyan-600">{companyProfile.phone}</span>
                    )}
                    {companyProfile?.address && (
                      <span className="text-xs text-gray-400">{companyProfile.address}</span>
                    )}
                  </div>
                  {companyProfile?.industry && (
                    <p className="text-xs text-gray-400 mt-0.5 italic">{companyProfile.industry}</p>
                  )}
                </div>
                {/* Invoice badge */}
                <div className="flex-shrink-0 text-right">
                  <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold tracking-widest uppercase">Invoice</span>
                  <p className="text-xs text-gray-400 mt-1">{invoicePeriodLabel}</p>
                </div>
              </div>
              {/* Divider line in orange */}
              <div className="mt-4 h-0.5 bg-gradient-to-r from-orange-400 via-orange-300 to-transparent rounded" />
              <p className="text-xs text-gray-500 mt-2">
                Preparing invoice for <span className="font-semibold text-gray-800">{clientDisplayName}</span>
              </p>
            </div>

            <div className="p-5 space-y-4">
              {/* Invoice inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-cyan-600 mb-1">Invoice Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={invoiceAmount}
                    onChange={(e) => setInvoiceAmount(e.target.value)}
                    className="w-full border border-orange-200 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    placeholder="Enter invoice amount"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-cyan-600 mb-1">Slogan <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={invoiceSlogan}
                    onChange={(e) => setInvoiceSlogan(e.target.value)}
                    className="w-full border border-orange-200 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    placeholder="Powered by intelligent dairy operations"
                  />
                </div>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                  <p className="text-xs text-cyan-600 font-medium mb-1">Purchases <span className="text-gray-400">({invoicePurchases.length} records)</span></p>
                  <p className="font-bold text-gray-900">{formatCurrency(invoicePurchasesTotal)}</p>
                </div>
                <div className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                  <p className="text-xs text-cyan-600 font-medium mb-1">Payments <span className="text-gray-400">({invoicePayments.length} records)</span></p>
                  <p className="font-bold text-gray-900">{formatCurrency(invoicePaymentsTotal)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">All records for the selected period will be included in the PDF, regardless of current page view.</p>
            </div>

            <div className="px-5 py-4 border-t border-orange-100 flex justify-end gap-2 bg-orange-50/40">
              <button
                type="button"
                onClick={() => setShowInvoiceModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateInvoicePdf}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold text-sm hover:from-orange-600 hover:to-orange-700 transition shadow"
              >
                Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-700">
                Confirm client deletion
              </h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-500 hover:text-gray-700"
                disabled={deleting}
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Please type <span className="font-semibold">{client?.otherName}</span> or{" "}
              <span className="font-semibold">{companyName}</span> to confirm deletion.
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type confirmation text"
              disabled={deleting}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteInput("");
                }}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClient}
                className={`px-4 py-2 rounded text-white transition ${deleting ? "bg-red-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewClientModal;
